/**
 * VMware monitoring — 1-1 parity with @console ManageMonitoring.
 *
 * Derives the metric list DYNAMICALLY from the `/stats` response.
 * The backend returns `{ performanceStats: { queriedPerformanceStats: [...] } }`
 * where each entry has:
 *   name, data[], pointStart (ms epoch), pointInterval (ms), legendItem.units
 *
 * Mirrors @console's UsageTrendsChart approach: expand pointStart + pointInterval
 * to build timestamps, then render one Chart per metric.
 *
 * Falls back gracefully to the flat key-value shape (cpu_usage / memory_usage /
 * disk_read_kbps / …) used by older backend versions, so no regression.
 *
 * Time-range: SegmentedControl presets (1h / 4h) matching @console's
 * QuickDateRangePicker items=['realtime','4h']. Refetches every 60s.
 */

import { useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Button } from '@vnetwork/ui/primitives';
import { Section } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import { Chart } from '@vnetwork/ui/charts';
import { useInstanceMonitoring } from '@/api/vmware/instances';
import { DateRangePicker, rangeFor } from '../../compute/components/DateRangePicker';

// ---- VMware queriedPerformanceStats shape --------------------------------

interface PerformanceStat {
  name: string;
  /** Metric group — "CPU" | "Memory" | "Disk" | "Network". */
  groupName?: string;
  data: number[];
  pointStart?: number;   // epoch ms of first sample
  pointInterval?: number; // ms between samples
  legendItem?: {
    /** Short metric label, e.g. "Ready", "Usage" — preferred over the long `name`. */
    measurement?: string;
    units?: string;
    latest?: number;
    max?: number;
    min?: number;
    average?: number;
    [k: string]: unknown;
  };
  [k: string]: unknown;
}

/** Drill to queriedPerformanceStats regardless of the enclosing wrapper. The hook
 *  unwraps to the `data` object `{ summary: { performanceStats: { queriedPerformanceStats } } }`,
 *  but older/edge responses may expose `performanceStats` at the root — accept both. */
function findQps(r: Record<string, unknown>): PerformanceStat[] {
  const summary = r.summary as Record<string, unknown> | undefined;
  const ps =
    (summary?.performanceStats as Record<string, unknown> | undefined) ??
    (r.performanceStats as Record<string, unknown> | undefined);
  const qps = ps?.queriedPerformanceStats;
  return Array.isArray(qps) ? (qps as PerformanceStat[]) : [];
}

// ---- Helpers ---------------------------------------------------------------

function hhmmss(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** Expand a queriedPerformanceStats entry into `{ t, v }` chart rows. */
function statToRows(stat: PerformanceStat): { t: string; v: number }[] {
  const start    = stat.pointStart    ?? 0;
  const interval = stat.pointInterval ?? 20_000; // default 20s VMware sampling
  return (stat.data ?? []).map((v, i) => ({
    t: hhmmss(start + i * interval),
    v: Number(v ?? 0),
  }));
}

/** Try the @console queriedPerformanceStats shape first, then fall back to the
 *  flat key-value shape used by older backend responses. */
function deriveCharts(
  raw: unknown,
): { title: string; unit: string; data: { t: string; v: number }[] }[] {
  if (!raw || typeof raw !== 'object') return [];
  const r = raw as Record<string, unknown>;

  // --- Shape A: (data.)summary.performanceStats.queriedPerformanceStats[] ------
  // The canonical VMware vSphere stats format @console reads. Title uses the short
  // legendItem.measurement (e.g. "Ready") prefixed by groupName (CPU/Memory/…),
  // NOT the long sentence `name`.
  const qps = findQps(r);
  if (qps.length > 0) {
    return qps
      .map((stat) => {
        const label = stat.legendItem?.measurement || stat.name;
        const group = stat.groupName ? `${stat.groupName} · ` : '';
        return {
          title: `${group}${label}`,
          unit:  stat.legendItem?.units ?? stat.yAxis as string ?? '',
          data:  statToRows(stat),
        };
      })
      .filter((c) => c.data.length > 0);
  }

  // --- Shape B (fallback): flat parallel arrays with a `timestamps` key ----
  // Matches what the current MonitoringTab handled before this parity fix.
  const timestamps: string[] = Array.isArray(r.timestamps)
    ? (r.timestamps as (string | number)[]).map((ts) => {
        const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      })
    : [];

  function flatToRows(key: string): { t: string; v: number }[] {
    const arr = r[key];
    if (!Array.isArray(arr)) return [];
    // Sub-shape: `[{ timestamp, value }]`
    if (arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null && 'value' in (arr[0] as object)) {
      return (arr as { timestamp: string | number; value: number }[]).map((p) => ({
        t: new Date(typeof p.timestamp === 'number' ? p.timestamp * 1000 : p.timestamp)
          .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        v: Number(p.value ?? 0),
      }));
    }
    // Sub-shape: plain number[] with parallel `timestamps`
    return (arr as number[]).map((v, i) => ({ t: timestamps[i] ?? String(i), v: Number(v ?? 0) }));
  }

  const candidates = [
    { key: 'cpu_usage',       title: 'CPU usage',    unit: '%'    },
    { key: 'memory_usage',    title: 'Memory usage', unit: '%'    },
    { key: 'disk_read_kbps',  title: 'Disk read',    unit: 'Kbps' },
    { key: 'disk_write_kbps', title: 'Disk write',   unit: 'Kbps' },
    { key: 'net_rx_kbps',     title: 'Network in',   unit: 'Kbps' },
    { key: 'net_tx_kbps',     title: 'Network out',  unit: 'Kbps' },
  ];
  return candidates
    .map((c) => ({ ...c, data: flatToRows(c.key) }))
    .filter((c) => c.data.length > 0);
}

// ---- Component -------------------------------------------------------------

export function MonitoringTab({ serviceId, instanceId }: { serviceId: string; instanceId: string }) {
  // @console supports 'realtime' (≈1h) and '4h'. Map to our DateRangePicker presets.
  const [preset, setPreset] = useState('1h');
  const range = useMemo(() => rangeFor(preset), [preset]);

  const { data: stats, isLoading, isError, refetch } = useInstanceMonitoring(
    serviceId,
    instanceId,
    { from: range.gte, to: range.lte },
  );

  // Derive charts dynamically — no hardcoded metric list.
  const charts = useMemo(() => deriveCharts(stats), [stats]);

  return (
    <Section
      title="Monitoring"
      description="CPU, memory, disk and network trends for this virtual machine."
      actions={<DateRangePicker value={preset} onChange={setPreset} onRefresh={() => void refetch()} />}
    >
      {isError ? (
        <EmptyState
          icon={<Activity size={28} />}
          title="Couldn't load metrics"
          description="Something went wrong fetching monitoring data for this instance."
          actions={<Button variant="secondary" onClick={() => void refetch()}>Retry</Button>}
        />
      ) : !isLoading && charts.length === 0 ? (
        <EmptyState
          icon={<Activity size={28} />}
          title="No metrics"
          description="No monitoring samples are available for the selected range."
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(360px, 100%), 1fr))', gap: 'var(--vn-space-4)' }}>
          {charts.map((c) => (
            <Card key={c.title}>
              <CardHeader>
                <CardTitle>
                  {c.title}{c.unit ? <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400 }}> ({c.unit})</span> : null}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <Chart
                  type="area"
                  data={c.data}
                  xKey="t"
                  series={[{ key: 'v', label: c.title }]}
                  height={200}
                  showLegend={false}
                  showGrid
                />
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}
